import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useTerritoires(getAuthHeader) {
  const [territoires, setTerritoires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dispositifs, setDispositifs] = useState([]);
  const [pays, setPays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTerritoire, setEditingTerritoire] = useState(null);
  const [selectedTerritoires, setSelectedTerritoires] = useState([]);
  const [backendStats, setBackendStats] = useState(null);
  
  // Import/Export states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importStats, setImportStats] = useState({ total: 0, valid: 0, errors: [] });
  const fileInputRef = useRef(null);
  
  // Filtres
  const [filterActif, setFilterActif] = useState('all');
  const [filterPays, setFilterPays] = useState('all');
  const [filterDispositif, setFilterDispositif] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  
  // Formulaire
  const [formData, setFormData] = useState({
    nom: '',
    code: '',
    pays_code: '',
    pays_nom: '',
    dispositifs: [],
    actif: true,
    description: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, [showArchived]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [territoiresRes, dispositifsRes, paysRes, statsRes] = await Promise.all([
        axios.get(`${API}/territoires?include_archive=${showArchived}`, { headers: getAuthHeader() }),
        axios.get(`${API}/territoires/dispositifs`, { headers: getAuthHeader() }),
        axios.get(`${API}/territoires/pays`, { headers: getAuthHeader() }),
        axios.get(`${API}/territoires/stats`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
      ]);
      
      setTerritoires(territoiresRes.data);
      setDispositifs(dispositifsRes.data.dispositifs);
      setPays(paysRes.data.pays);
      setBackendStats(statsRes.data);
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingTerritoire(null);
    setFormData({
      nom: '',
      code: '',
      pays_code: '',
      pays_nom: '',
      dispositifs: [],
      actif: true,
      description: ''
    });
    setShowModal(true);
  };

  const handleEdit = (territoire) => {
    setEditingTerritoire(territoire);
    setFormData({
      nom: territoire.nom,
      code: territoire.code,
      pays_code: territoire.pays_code,
      pays_nom: territoire.pays_nom,
      dispositifs: territoire.dispositifs || [],
      actif: territoire.actif,
      description: territoire.description || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.nom || !formData.code || !formData.pays_code) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }

      if (editingTerritoire) {
        await axios.put(
          `${API}/territoires/${editingTerritoire.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Territoire mis à jour avec succès !');
      } else {
        await axios.post(
          `${API}/territoires`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Territoire créé avec succès !');
      }

      setShowModal(false);
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (territoireId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce territoire ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/territoires/${territoireId}`, { headers: getAuthHeader() });
      toast.success('Territoire supprimé avec succès !');
      fetchInitialData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedTerritoires.length === 0) {
      toast.error('Aucun territoire sélectionné');
      return;
    }

    const confirmMessage = {
      'activer': 'Activer',
      'desactiver': 'Désactiver',
      'archiver': 'Archiver',
      'supprimer': 'SUPPRIMER DÉFINITIVEMENT'
    }[action];

    if (!window.confirm(`${confirmMessage} ${selectedTerritoires.length} territoire(s) ?`)) {
      return;
    }

    try {
      await axios.post(
        `${API}/territoires/bulk-action`,
        {
          territoire_ids: selectedTerritoires,
          action: action
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Action "${action}" effectuée avec succès !`);
      setSelectedTerritoires([]);
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'action');
    }
  };

  const handleToggleDispositif = (dispositif) => {
    const newDispositifs = formData.dispositifs.includes(dispositif)
      ? formData.dispositifs.filter(d => d !== dispositif)
      : [...formData.dispositifs, dispositif];
    setFormData({ ...formData, dispositifs: newDispositifs });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTerritoires(filteredTerritoires.map(t => t.id));
    } else {
      setSelectedTerritoires([]);
    }
  };

  const handleSelectTerritoire = (territoireId) => {
    setSelectedTerritoires(prev =>
      prev.includes(territoireId)
        ? prev.filter(id => id !== territoireId)
        : [...prev, territoireId]
    );
  };

  const handlePaysChange = (paysCode) => {
    const selectedPays = pays.find(p => p.code === paysCode);
    setFormData({
      ...formData,
      pays_code: paysCode,
      pays_nom: selectedPays ? selectedPays.nom : ''
    });
  };

  const filteredTerritoires = territoires.filter(territoire => {
    const matchesSearch = !searchQuery ||
      territoire.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      territoire.code.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesActif = filterActif === 'all' ||
      (filterActif === 'actif' && territoire.actif) ||
      (filterActif === 'inactif' && !territoire.actif);
    const matchesPays = filterPays === 'all' || territoire.pays_code === filterPays;
    const matchesDispositif = filterDispositif === 'all' ||
      (territoire.dispositifs && territoire.dispositifs.includes(filterDispositif));
    
    return matchesSearch && matchesActif && matchesPays && matchesDispositif;
  });

  // Stats dynamiques calculées sur les territoires (fallback si backend n'a pas répondu)
  const territoireStats = React.useMemo(() => {
    const total = territoires.length;
    const actifs = territoires.filter(t => t.actif && !t.archive).length;
    const inactifs = territoires.filter(t => !t.actif && !t.archive).length;
    const archives = territoires.filter(t => t.archive).length;
    const paysCount = new Set(territoires.map(t => t.pays_code).filter(Boolean)).size;
    const dispositifsCovered = new Set(territoires.flatMap(t => t.dispositifs || [])).size;
    const couvertureMoyenne = total ? Math.round(territoires.reduce((acc, t) => acc + (t.dispositifs?.length || 0), 0) / total * 10) / 10 : 0;
    // Top 5 pays par nombre de territoires (enrichi avec données backend si dispo)
    const paysGroup = territoires.reduce((acc, t) => {
      if (!t.pays_code) return acc;
      acc[t.pays_code] = acc[t.pays_code] || { code: t.pays_code, nom: t.pays_nom || t.pays_code, count: 0, actifs: 0, souscriptions: 0 };
      acc[t.pays_code].count += 1;
      if (t.actif && !t.archive) acc[t.pays_code].actifs += 1;
      return acc;
    }, {});
    // Enrichir avec souscriptions du backend
    if (backendStats?.par_pays) {
      backendStats.par_pays.forEach(p => {
        if (paysGroup[p.code]) {
          paysGroup[p.code].souscriptions = p.souscriptions_domium || 0;
        }
      });
    }
    const topPays = Object.values(paysGroup).sort((a, b) => b.count - a.count).slice(0, 5);
    return { total, actifs, inactifs, archives, paysCount, dispositifsCovered, couvertureMoyenne, topPays };
  }, [territoires, backendStats]);

  // Export functions
  const exportToJSON = () => {
    const dataToExport = {
      territoires: territoires,
      dispositifs_disponibles: dispositifs,
      pays_disponibles: pays,
      exported_at: new Date().toISOString(),
      version: '1.0',
      total_count: territoires.length
    };
    
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `territoires_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export JSON réussi !');
  };

  const exportToCSV = () => {
    const headers = [
      'Nom', 'Code', 'Pays Code', 'Pays Nom', 'Dispositifs', 'Actif', 'Archivé', 'Description'
    ];
    
    const rows = territoires.map(t => [
      t.nom,
      t.code,
      t.pays_code,
      t.pays_nom,
      (t.dispositifs || []).join(', '),
      t.actif ? 'Oui' : 'Non',
      t.archive ? 'Oui' : 'Non',
      t.description || ''
    ]);

    const csvContent = [headers.join(';'), ...rows.map(r => r.map(cell => `"${cell}"`).join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `territoires_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export CSV réussi !');
  };

  const exportToExcel = () => {
    const headers = [
      'Nom', 'Code', 'Pays Code', 'Pays Nom', 'Dispositifs', 'Actif', 'Archivé', 'Description'
    ];
    
    const rows = territoires.map(t => [
      t.nom,
      t.code,
      t.pays_code,
      t.pays_nom,
      (t.dispositifs || []).join(', '),
      t.actif ? 'Oui' : 'Non',
      t.archive ? 'Oui' : 'Non',
      t.description || ''
    ]);

    const csvContent = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `territoires_${new Date().toISOString().split('T')[0]}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export Excel réussi !');
  };

  // Import functions
  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        let parsedData = [];
        const errors = [];
        
        if (file.name.endsWith('.json')) {
          const data = JSON.parse(content);
          if (data.territoires && Array.isArray(data.territoires)) {
            parsedData = data.territoires;
          } else if (Array.isArray(data)) {
            parsedData = data;
          } else {
            toast.error('Format JSON invalide. Attendu: { territoires: [...] } ou [...]');
            return;
          }
        } else if (file.name.endsWith('.csv')) {
          const lines = content.split('\n');
          const headers = lines[0].split(';').map(h => h.replace(/"/g, '').trim().toLowerCase());
          
          lines.slice(1).filter(l => l.trim()).forEach((line, idx) => {
            try {
              const values = line.split(';').map(v => v.replace(/"/g, '').trim());
              const nomIdx = headers.indexOf('nom');
              const codeIdx = headers.indexOf('code');
              const paysCodeIdx = headers.indexOf('pays code');
              const paysNomIdx = headers.indexOf('pays nom');
              const dispositifsIdx = headers.indexOf('dispositifs');
              const actifIdx = headers.indexOf('actif');
              const descIdx = headers.indexOf('description');

              if (nomIdx === -1 || codeIdx === -1) {
                errors.push(`Ligne ${idx + 2}: colonnes 'Nom' et 'Code' requises`);
                return;
              }

              parsedData.push({
                nom: values[nomIdx] || '',
                code: values[codeIdx] || '',
                pays_code: paysCodeIdx !== -1 ? values[paysCodeIdx] : 'FR',
                pays_nom: paysNomIdx !== -1 ? values[paysNomIdx] : 'France',
                dispositifs: dispositifsIdx !== -1 && values[dispositifsIdx] 
                  ? values[dispositifsIdx].split(',').map(d => d.trim()).filter(d => d)
                  : [],
                actif: actifIdx !== -1 ? values[actifIdx]?.toLowerCase() === 'oui' : true,
                description: descIdx !== -1 ? values[descIdx] : ''
              });
            } catch {
              errors.push(`Ligne ${idx + 2}: erreur de parsing`);
            }
          });
        } else {
          toast.error('Format de fichier non supporté. Utilisez JSON ou CSV.');
          return;
        }

        // Validate data
        const validData = parsedData.filter((t, idx) => {
          if (!t.nom || !t.code) {
            errors.push(`Entrée ${idx + 1}: nom et code requis`);
            return false;
          }
          return true;
        });

        setImportData(validData);
        setImportStats({
          total: parsedData.length,
          valid: validData.length,
          errors: errors
        });
        setImportDialogOpen(true);
      } catch {
        toast.error('Erreur lors de la lecture du fichier');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const confirmImport = async () => {
    if (!importData || importData.length === 0) {
      toast.error('Aucune donnée valide à importer');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const territoire of importData) {
        try {
          // Check if territoire already exists by code
          const existing = territoires.find(t => t.code === territoire.code);
          
          if (existing) {
            // Update existing
            await axios.put(
              `${API}/territoires/${existing.id}`,
              territoire,
              { headers: getAuthHeader() }
            );
          } else {
            // Create new
            await axios.post(
              `${API}/territoires`,
              territoire,
              { headers: getAuthHeader() }
            );
          }
          successCount++;
        } catch {
          errorCount++;
        }
      }

      toast.success(`Import terminé: ${successCount} réussi(s), ${errorCount} erreur(s)`);
      setImportDialogOpen(false);
      setImportData(null);
      fetchInitialData();
    } catch {
      toast.error('Erreur lors de l\'import');
    }
  };

  return { getAuthHeader, territoires, setTerritoires, loading, setLoading, dispositifs, setDispositifs, pays, setPays, showModal, setShowModal, editingTerritoire, setEditingTerritoire, selectedTerritoires, setSelectedTerritoires, backendStats, setBackendStats, importDialogOpen, setImportDialogOpen, importData, setImportData, importStats, setImportStats, fileInputRef, filterActif, setFilterActif, filterPays, setFilterPays, filterDispositif, setFilterDispositif, searchQuery, setSearchQuery, showArchived, setShowArchived, formData, setFormData, fetchInitialData, handleCreateNew, handleEdit, handleSave, handleDelete, handleBulkAction, handleToggleDispositif, handleSelectAll, handleSelectTerritoire, handlePaysChange, filteredTerritoires, territoireStats, exportToJSON, exportToCSV, exportToExcel, handleFileImport, confirmImport };
}
